import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type UrlStateOptions = {
  /** Параметры, которые не попадают в URL (например, только UI-состояние). */
  readonly defaults?: Readonly<Record<string, string | null>>;
};

/**
 * Состояние вида в URL (docs/06 §5.2): обновление страницы не сбрасывает
 * фильтры, спринт, группировку и открытую задачу.
 */
export function useUrlState(): {
  readonly params: URLSearchParams;
  get: (key: string) => string | null;
  set: (patch: Readonly<Record<string, string | number | null | undefined>>) => void;
  clear: (keys: readonly string[]) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => new URLSearchParams(searchParams), [searchParams]);

  const get = useCallback((key: string) => searchParams.get(key), [searchParams]);

  const set = useCallback(
    (patch: Readonly<Record<string, string | number | null | undefined>>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === undefined || value === '') {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clear = useCallback(
    (keys: readonly string[]) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const key of keys) {
            next.delete(key);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { params, get, set, clear };
}

/** Стабильная строка-ключ для React Query по текущему набору параметров. */
export function serializeParams(params: Readonly<Record<string, string | number | null | undefined>>): string {
  return Object.entries(params)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${String(value)}`)
    .join('&');
}
